import {
    ArrowBack as ArrowBackIcon,
    Email as EmailIcon,
    Person as PersonIcon,
    Phone as PhoneIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import axios from 'axios';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

const EnquiryEdit = () => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const apiURL = process.env.REACT_APP_API;

    const enquiryId = searchParams.get('id');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Status options
    const statusOptions = [
        { value: 'pending', label: 'Pending' },
        { value: 'converted', label: 'Converted' },
        { value: 'rejected', label: 'Rejected' }
    ];

    // Validation schema
    const validationSchema = Yup.object({
        enq_student_name: Yup.string()
            .required('Student name is required')
            .min(2, 'Name must be at least 2 characters'),
        enq_student_gender: Yup.string()
            .required('Gender is required')
            .oneOf(['male', 'female', 'other'], 'Invalid gender'),
        enq_student_mobile: Yup.string()
            .required('Mobile number is required')
            .matches(/^[0-9]{10}$/, 'Mobile number must be exactly 10 digits'),
        enq_student_email: Yup.string()
            .required('Email is required')
            .email('Invalid email address'),
        enq_status: Yup.string()
            .required('Status is required')
            .oneOf(['pending', 'converted', 'rejected'], 'Invalid status')
    });

    // Formik setup
    const formik = useFormik({
        initialValues: {
            enq_student_name: '',
            enq_student_gender: 'male',
            enq_student_mobile: '',
            enq_student_email: '',
            enq_status: 'pending'
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setSubmitting(true);
            try {
                const response = await axios.post(
                    `${apiURL}/franchise/enquiry/update`,
                    {
                        enquiryid: enquiryId,
                        ...values
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    }
                );

                if (response.data.status === 'success' || response.data.status === true) {
                    toast.success('Enquiry updated successfully');
                    enqueueSnackbar('Enquiry updated successfully', { variant: 'success' });
                    // Refresh the form data after successful update
                    fetchEnquiryDetails();
                    navigate(-1);
                } else {
                    toast.error(response.data.message || 'Failed to update enquiry');
                    enqueueSnackbar(response.data.message || 'Failed to update enquiry', { variant: 'error' });
                }
            } catch (error) {
                console.error('Error updating enquiry:', error);
                toast.error(error.response?.data?.message || 'Failed to update enquiry');
                enqueueSnackbar('Failed to update enquiry', { variant: 'error' });
            } finally {
                setSubmitting(false);
            }
        }
    });

    // Fetch enquiry details
    const fetchEnquiryDetails = async () => {
        if (!enquiryId) {
            toast.error('Enquiry ID is required');
            navigate(-1);
            return;
        }

        setLoading(true);
        try {
            const response = await axios.get(`${apiURL}/franchise/enquiry/details?enquiryid=${enquiryId}`);

            if (response.data.status === 'success' || response.data.status === true) {
                const data = response.data.data;
                formik.setValues({
                    enq_student_name: data.enq_student_name || '',
                    enq_student_gender: data.enq_student_gender || 'male',
                    enq_student_mobile: data.enq_student_mobile || '',
                    enq_student_email: data.enq_student_email || '',
                    enq_status: data.enq_status || 'pending'
                });
            } else {
                toast.error(response.data.message || 'Failed to fetch enquiry details');
                navigate(-1);
            }
        } catch (error) {
            console.error('Error fetching enquiry:', error);
            toast.error(error.response?.data?.message || 'Failed to fetch enquiry details');
            navigate(-1);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEnquiryDetails();
    }, [enquiryId]);

    const handleBack = () => {
        navigate(-1);
    };

    if (loading) {
        return (
            <Box sx={{ py: 4 }} className="content">
                <Container maxWidth="md">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                        <Typography variant="h6">Loading...</Typography>
                    </Box>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ py: 4 }} className="content">
            <Container maxWidth="lg">
                {/* Header */}
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                        variant="outlined"
                    >
                        Back
                    </Button>
                    <Typography variant="h4" fontWeight={600}>
                        Edit Enquiry
                    </Typography>
                </Stack>

                {/* Form */}
                <Card sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ p: 4 }}>
                        <form onSubmit={formik.handleSubmit}>
                            <Grid container spacing={3}>
                                {/* Student Name */}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        id="enq_student_name"
                                        name="enq_student_name"
                                        label="Student Name"
                                        value={formik.values.enq_student_name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.enq_student_name && Boolean(formik.errors.enq_student_name)}
                                        helperText={formik.touched.enq_student_name && formik.errors.enq_student_name}
                                        InputProps={{
                                            startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
                                        }}
                                    />
                                </Grid>

                                {/* Gender */}
                                <Grid item xs={12}>
                                    <FormControl
                                        component="fieldset"
                                        error={formik.touched.enq_student_gender && Boolean(formik.errors.enq_student_gender)}
                                    >
                                        <FormLabel component="legend">Gender</FormLabel>
                                        <RadioGroup
                                            row
                                            id="enq_student_gender"
                                            name="enq_student_gender"
                                            value={formik.values.enq_student_gender}
                                            onChange={formik.handleChange}
                                        >
                                            <FormControlLabel value="male" control={<Radio />} label="Male" />
                                            <FormControlLabel value="female" control={<Radio />} label="Female" />
                                            <FormControlLabel value="other" control={<Radio />} label="Other" />
                                        </RadioGroup>
                                        {formik.touched.enq_student_gender && formik.errors.enq_student_gender && (
                                            <FormHelperText>{formik.errors.enq_student_gender}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>

                                {/* Mobile */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        id="enq_student_mobile"
                                        name="enq_student_mobile"
                                        label="Mobile Number"
                                        value={formik.values.enq_student_mobile}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.enq_student_mobile && Boolean(formik.errors.enq_student_mobile)}
                                        helperText={formik.touched.enq_student_mobile && formik.errors.enq_student_mobile}
                                        InputProps={{
                                            startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} />
                                        }}
                                    />
                                </Grid>

                                {/* Email */}
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        id="enq_student_email"
                                        name="enq_student_email"
                                        label="Email Address"
                                        type="email"
                                        value={formik.values.enq_student_email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.enq_student_email && Boolean(formik.errors.enq_student_email)}
                                        helperText={formik.touched.enq_student_email && formik.errors.enq_student_email}
                                        InputProps={{
                                            startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
                                        }}
                                    />
                                </Grid>

                                {/* Status */}
                                <Grid item xs={12}>
                                    <FormControl
                                        fullWidth
                                        error={formik.touched.enq_status && Boolean(formik.errors.enq_status)}
                                    >
                                        <InputLabel id="enq_status-label">Status</InputLabel>
                                        <Select
                                            labelId="enq_status-label"
                                            id="enq_status"
                                            name="enq_status"
                                            value={formik.values.enq_status}
                                            label="Status"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                        >
                                            {statusOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {formik.touched.enq_status && formik.errors.enq_status && (
                                            <FormHelperText>{formik.errors.enq_status}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>

                                {/* Submit Button */}
                                <Grid item xs={12}>
                                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                                        <Button
                                            variant="outlined"
                                            onClick={handleBack}
                                            disabled={submitting}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            startIcon={<SaveIcon />}
                                            disabled={submitting}
                                        >
                                            {submitting ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </form>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default EnquiryEdit;
